import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Package, Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const Register = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      await register(email, password, name);
      toast.success('Account created successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      {/* Left Side - Branding */}
      <div className="auth-image-side">
        <div className="text-center px-12">
          <div className="flex items-center justify-center mb-8">
            <div className="w-16 h-16 bg-orange-500 rounded-sm flex items-center justify-center">
              <Package className="w-10 h-10 text-white" />
            </div>
          </div>
          <h1 className="font-heading text-4xl font-bold text-white uppercase tracking-tight mb-4">
            SupplyTrack Pro
          </h1>
          <p className="text-slate-300 text-lg">
            Start managing your supplement inventory today
          </p>
          <div className="mt-12">
            <img 
              src="https://images.unsplash.com/photo-1763757933292-d8290692edde?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxwcm90ZWluJTIwcG93ZGVyJTIwc3VwcGxlbWVudCUyMGphciUyMHdoaXRlJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3NzIzMjE4Mjh8MA&ixlib=rb-4.1.0&q=85"
              alt="Supplements"
              className="rounded-sm shadow-2xl max-w-sm mx-auto opacity-90"
            />
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="auth-form-side">
        <Card className="w-full max-w-md border-0 shadow-none">
          <CardHeader className="text-center pb-2">
            <div className="flex items-center justify-center mb-4 lg:hidden">
              <div className="w-12 h-12 bg-slate-900 rounded-sm flex items-center justify-center">
                <Package className="w-7 h-7 text-white" />
              </div>
            </div>
            <CardTitle className="font-heading text-3xl font-bold uppercase tracking-tight text-slate-900">
              Create Account
            </CardTitle>
            <CardDescription className="text-slate-500 mt-2">
              Get started with your inventory management
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-medium text-slate-700">
                  Full Name
                </Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="John Doe"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  data-testid="register-name-input"
                  className="h-11 rounded-sm border-slate-200 focus:border-slate-900 focus:ring-slate-900"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                  Email Address
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="register-email-input"
                  className="h-11 rounded-sm border-slate-200 focus:border-slate-900 focus:ring-slate-900"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                  Password
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Min. 6 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="register-password-input"
                    className="h-11 rounded-sm border-slate-200 focus:border-slate-900 focus:ring-slate-900 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-sm font-medium text-slate-700">
                  Confirm Password
                </Label>
                <Input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  data-testid="register-confirm-password-input"
                  className="h-11 rounded-sm border-slate-200 focus:border-slate-900 focus:ring-slate-900"
                />
              </div>
              <Button 
                type="submit" 
                disabled={loading}
                data-testid="register-submit-button"
                className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-semibold uppercase tracking-wide rounded-sm btn-active-scale mt-2"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 mr-2 spinner" /> Creating Account...</>
                ) : (
                  'Create Account'
                )}
              </Button>
            </form>
            <div className="mt-6 text-center">
              <p className="text-slate-500 text-sm">
                Already have an account?{' '}
                <Link 
                  to="/login" 
                  className="text-orange-500 hover:text-orange-600 font-medium"
                  data-testid="login-link"
                >
                  Sign In
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Register;
